import { type Assessment, signalInWords } from '../../api/exams'

/**
 * The judgement of one sitting.
 *
 * **One judgement for the whole sitting**, however many standards the tasks were measured by. That
 * is not a summary of several — there is one, and this draws it.
 *
 * Three things, and the way they are drawn is the design rule rather than decoration:
 *
 * - the **mark stands beside its scale**, always. `14` means nothing without `1-18`, and the scale is
 *   whatever the assistant chose for this paper.
 * - the **traffic light is never colour alone.** It carries the word — *Good*, *Nearly there*,
 *   *Shaky*, *Not yet* — because a colour is not readable to everybody and is not readable at all in
 *   a screen reader.
 * - the **points are grouped by what they are**: what went well, what did not, and what is merely
 *   worth knowing. In that order, and the empty groups are simply absent.
 * - a point that names a **passage** is not here at all: it is read *in the answer*, at the end of
 *   the paragraph its passage is in. Reading a correction in two places was the complaint this area
 *   started from, and leaving a copy here would be two places again.
 * - unless the passage is **no longer in the text** — somebody wrote on after the judgement. Then
 *   the point is read here, with a line saying the passage is gone. Nothing is approximated and
 *   nothing is dropped: the correction is what was written, whether or not MyLib can still point at
 *   what it was about.
 */
export interface ResultProps {
  readonly assessment: Assessment
  /** The point being looked at, by its place in `assessment.remarks`, or `null`. */
  readonly selected?: number | null
  /** The points whose passage could not be found in the answer, by the same index. */
  readonly unfound?: readonly number[]
}

export function Result({ assessment, selected = null, unfound = [] }: ResultProps) {
  const groups = [
    { tone: 'GOOD' as const, heading: 'What went well' },
    { tone: 'BAD' as const, heading: 'What did not' },
    { tone: 'NEUTRAL' as const, heading: 'Worth knowing' },
  ]

  return (
    <section className="result" aria-label="The judgement of this sitting">
      <header className="result__head">
        <p className="result__mark">
          <span className="result__grade">{assessment.grade}</span>
          <span className="result__scale">on {assessment.scale}</span>
        </p>
        <p className="result__signal" data-signal={assessment.signal.toLowerCase()}>
          {signalInWords(assessment.signal)}
        </p>
      </header>

      {assessment.summary !== '' && <p className="result__summary">{assessment.summary}</p>}

      {groups.map((group) => {
        // The index is the point's name everywhere — in the mark at the passage, in the selection,
        // in what could not be found — so it is taken before the grouping and never after it.
        // The index is the point's name everywhere — in the mark at the passage, in the selection,
        // in what could not be found — so it is taken before the grouping and never after it. A
        // point that names a passage is left out here: it is read at that passage, in the answer.
        // It comes back only where the passage could not be found.
        const points = assessment.remarks
          .map((remark, index) => ({ remark, index }))
          .filter((one) => one.remark.tone === group.tone)
          .filter((one) => one.remark.quote === null || unfound.includes(one.index))
        if (points.length === 0) {
          return null
        }
        return (
          <div className="result__group" key={group.tone} data-tone={group.tone.toLowerCase()}>
            <h3 className="result__heading">{group.heading}</h3>
            <ul className="result__points">
              {points.map(({ remark, index }) => {
                const gone = remark.quote !== null && unfound.includes(index)
                return (
                  <li
                    className="result__point"
                    key={`${group.tone}-${String(index)}`}
                    data-at={index === selected ? 'true' : undefined}
                  >
                    <p className="result__text">{remark.text}</p>
                    {gone && (
                      <p className="result__gone">
                        This passage is not in the answer any more: “{remark.quote}”
                      </p>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}

      <p className="result__when">Judged {new Date(assessment.assessedAt).toLocaleString()}</p>
    </section>
  )
}
